import React, { useEffect, useRef, useState } from 'react';
import { hotelOrgService } from '../services/hotelOrgService';
import { LoadingState } from './useHotels';

export interface HotelOrganization {
  id: number;
  name: string;
}

export interface UseHotelOrgParams {
  search?: string;
  limit?: number;
  primarySortKey?: string;
  primarySortOrder?: 'asc' | 'desc' | null;
  page?: number;
  acquiredOnly?: boolean;
}

export interface UseHotelOrgReturn {
  hotelOrganizations: HotelOrganization[];
  loadingState: LoadingState;
  setLoadingState: React.Dispatch<React.SetStateAction<LoadingState>>;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  refetch: () => void;
}

/**
 * Hook for searching hotel organizations
 * Used on the main Hotel Organizations page
 */
export const useHotelOrganizations = (
  params: UseHotelOrgParams = {}
): UseHotelOrgReturn => {
  const {
    search = '',
    limit = 10,
    primarySortKey,
    primarySortOrder,
    page,
    acquiredOnly
  } = params || {};
  const [hotelOrganizations, setHotelOrganizations] = React.useState<
    HotelOrganization[]
  >([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('initial');

  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasDataRef = useRef(false);
  const requestIdRef = useRef(0);
  const fetchHotelOrganizations = React.useCallback(async () => {
    console.log('Fetching hotel organizations with search:', search);
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const currentController = new AbortController();
    abortControllerRef.current = currentController;

    // Increment request ID for this new request
    const currentRequestId = ++requestIdRef.current;

    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!hasDataRef.current) {
      setLoadingState('initial');
    }
    try {
      const queryParams: Record<string, any> = {};
      queryParams.acquired_only = acquiredOnly;
      if (search) queryParams.search = search;

      // Add primary sort if provided
      if (primarySortKey && primarySortOrder) {
        queryParams.primarySortKey = primarySortKey;
        queryParams.primarySortOrder = primarySortOrder;
      }

      queryParams.page = page;
      queryParams.limit = limit;
      // setLoadingState('search');
      const hotelOrgResponse = await hotelOrgService.getHotelOrganizations(
        queryParams
      );
      const paginationData = (hotelOrgResponse as any)?.pagination;
      console.log('Fetched hotel organizations:', hotelOrgResponse);
      setHotelOrganizations(
        Array.isArray(hotelOrgResponse?.data) ? hotelOrgResponse.data : []
      );
      setTotalCount(paginationData?.totalCount || 0);
      setTotalPages(paginationData?.totalPages ?? 0);
      setHasNextPage(paginationData?.hasNextPage || false);
      setHasPrevPage(paginationData?.hasPrevPage || false);
      hasDataRef.current = true;
    } catch (error) {
      console.error('Error fetching hotel organizations:', error);
      setHotelOrganizations([]);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoadingState('idle');
      }
    }
  }, [search, primarySortKey, primarySortOrder, page, limit, acquiredOnly]);

  useEffect(() => {
    fetchHotelOrganizations();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchHotelOrganizations]);

  return {
    hotelOrganizations,
    loadingState,
    setLoadingState,
    totalCount,
    totalPages,
    hasNextPage,
    hasPrevPage,
    refetch: fetchHotelOrganizations,
  };
};
